import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  selectActiveApplicationDetails,
  selectActiveUserId
} from 'features/applications/applicationsSelectors';
import { fetchActiveApplicationDetails } from 'features/applications/applicationsThunks';
import { selectAppLocale } from 'features/locale/localeSelectors';
import { closeSidebar, setActiveUserId } from 'features/sidebar/sidebarSlice';

import useErrorCatch from 'hooks/useErrorCatch';
import { useAppDispatch, useAppSelector } from 'hooks/useTypedRedux';

import { Application } from 'types/applications';

const useGetApplication = (
  applicationIdProp?: string | null
): {
  application: Application | null;
  isLoading: boolean;
  fetchApplicationData: (applicationId: string, lang: string) => Promise<void>;
} => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const catchError = useErrorCatch();

  const applicationId = applicationIdProp || useAppSelector(selectActiveUserId);
  const activeApplication = useAppSelector(selectActiveApplicationDetails);
  const locale = useAppSelector(selectAppLocale);

  const application: Application | null =
    activeApplication?.id === applicationId ? activeApplication : null;

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchApplicationData = useCallback(
    async (applicationId: string, locale: string): Promise<void> => {
      try {
        setIsLoading(true);
        await dispatch(
          fetchActiveApplicationDetails({ applicationId, locale })
        );

        dispatch(setActiveUserId(applicationId));
      } catch (error) {
        catchError({
          error,
          default: {
            message: t('error.message.operationFailed'),
            description: t('error.message.fetchApplication')
          }
        });
        dispatch(closeSidebar());
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, dispatch, catchError]
  );

  useEffect(() => {
    if (!applicationId) {
      return;
    }
    fetchApplicationData(applicationId, locale);
  }, [applicationId, locale]);

  return { application, isLoading, fetchApplicationData };
};

export default useGetApplication;
